import type { ActivityContext, ActivityHandle } from '../core/activity';
import { mountKeypad } from './keypad';

export type Operator = '+' | '−' | '×' | '÷';

export interface Operand {
  first: number;
  second: number;
}

interface LevelDataFixed {
  randomOperands: false;
  shuffle: boolean;
  operands: Operand[];
}

interface LevelDataRandom {
  randomOperands: true;
  min: number;
  max: number;
  limit: number;
}

type LevelData = LevelDataFixed | LevelDataRandom;

export interface AlgebraLevel {
  level: number;
  objective: string;
  difficulty: number;
  data: LevelData[];
}

export interface AlgebraRunnerOptions {
  activityId: string;
  title: string;
  operator: Operator;
  compute(a: number, b: number): number;
  levels: AlgebraLevel[];
  questionsPerLevel?: number;
}

interface Question {
  a: number;
  b: number;
  answer: number;
}

export function runAlgebraActivity(ctx: ActivityContext, opts: AlgebraRunnerOptions): ActivityHandle {
  const questionsPerLevel = opts.questionsPerLevel ?? 10;
  const level = opts.levels.find((l) => l.level === ctx.level) ?? opts.levels[0];
  if (!level) {
    ctx.root.innerHTML = '<p>Nível não encontrado.</p>';
    return { unmount: () => { ctx.root.innerHTML = ''; } };
  }
  const questions = buildQuestions(level, opts, questionsPerLevel);

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">${opts.title}</div>
      <div class="activity-bar__level">Nível ${level.level} de ${opts.levels.length}</div>
    </header>
    <section class="activity-stage algebra-plus">
      <div class="feedback" aria-live="polite"></div>
      <div class="algebra-plus__progress" aria-live="polite"></div>
      <div class="algebra-plus__expr" aria-live="polite"></div>
      <div class="algebra-plus__answer" role="textbox" aria-label="Sua resposta">_</div>
      <div class="algebra-plus__keypad"></div>
    </section>
  `;
  ctx.root.appendChild(page);

  const progressEl = page.querySelector<HTMLElement>('.algebra-plus__progress')!;
  const exprEl = page.querySelector<HTMLElement>('.algebra-plus__expr')!;
  const answerEl = page.querySelector<HTMLElement>('.algebra-plus__answer')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  const backBtn = page.querySelector<HTMLElement>('.activity-bar__back')!;
  const keypadHost = page.querySelector<HTMLElement>('.algebra-plus__keypad')!;

  backBtn.addEventListener('click', () => ctx.onExit());

  let index = 0;
  let typed = '';
  let correctCount = 0;
  let wrongCount = 0; // respostas erradas (antes o erro era invisível — P-166 F1)

  function renderQuestion(): void {
    if (index >= questions.length) {
      finishLevel();
      return;
    }
    const q = questions[index]!;
    progressEl.textContent = `Questão ${index + 1} de ${questions.length}`;
    exprEl.textContent = `${q.a} ${opts.operator} ${q.b} = ?`;
    typed = '';
    answerEl.textContent = '_';
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedback.textContent = kind === 'ok' ? '✓ Correto!' : '✗ Tente de novo';
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 800);
  }

  function submit(): void {
    if (typed.length === 0) return;
    const q = questions[index]!;
    const value = parseInt(typed, 10);
    if (value === q.answer) {
      ctx.audio.play('correct');
      correctCount++;
      showFeedback('ok');
      index++;
      window.setTimeout(renderQuestion, 600);
    } else {
      ctx.audio.play('wrong');
      wrongCount++;
      showFeedback('err');
      typed = '';
      answerEl.textContent = '_';
    }
  }

  function pushDigit(d: string): void {
    if (typed.length >= 4) return;
    typed += d;
    answerEl.textContent = typed;
    ctx.audio.play('click');
  }

  function backspace(): void {
    if (typed.length === 0) return;
    typed = typed.slice(0, -1);
    answerEl.textContent = typed.length === 0 ? '_' : typed;
  }

  function finishLevel(): void {
    ctx.audio.play('win');
    ctx.onComplete(level.level, correctCount, {
      total: questions.length,
      tentativas: correctCount + wrongCount,
      erros: wrongCount,
      params: { op: opts.operator },
    });
    const resumo = wrongCount === 0
      ? `Você fechou as ${questions.length} contas de primeira!`
      : `Você fechou as ${questions.length} contas. Erros pelo caminho: ${wrongCount}.`;
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">${resumo}</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < opts.levels.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) {
      next.addEventListener('click', () => {
        window.location.hash = `/${opts.activityId}/${level.level + 1}`;
      });
    }
  }

  const onKey = (e: KeyboardEvent): void => {
    if (e.key >= '0' && e.key <= '9') pushDigit(e.key);
    else if (e.key === 'Backspace') backspace();
    else if (e.key === 'Enter') submit();
  };
  window.addEventListener('keydown', onKey);

  mountKeypad(keypadHost, {
    onDigit: pushDigit,
    onBackspace: backspace,
    onEnter: submit,
  });

  renderQuestion();

  return {
    unmount(): void {
      window.removeEventListener('keydown', onKey);
      ctx.root.innerHTML = '';
    },
  };
}

export function buildQuestions(level: AlgebraLevel, opts: AlgebraRunnerOptions, count: number): Question[] {
  const config = level.data[0];
  if (!config) return [];
  const list: Question[] = [];

  if (!config.randomOperands) {
    const ops: Operand[] = config.shuffle ? shuffle([...config.operands]) : [...config.operands];
    for (const op of ops) {
      list.push({ a: op.first, b: op.second, answer: opts.compute(op.first, op.second) });
    }
    return list;
  }

  // Random — segue convenção do GCompris-qt:
  // - subtração: left >= right (sem negativos)
  // - divisão: left é múltiplo de right (resultado inteiro)
  // - limit === 0 significa sem limite
  let safety = count * 50;
  while (list.length < count && safety-- > 0) {
    const pair = genPair(opts.operator, config.min, config.max);
    if (!pair) continue;
    const result = opts.compute(pair.a, pair.b);
    if (!Number.isFinite(result) || !Number.isInteger(result)) continue;
    if (config.limit !== 0 && result > config.limit) continue;
    list.push({ a: pair.a, b: pair.b, answer: result });
  }
  return list;
}

function genPair(operator: Operator, min: number, max: number): { a: number; b: number } | null {
  switch (operator) {
    case '+':
    case '×':
      return { a: randInt(min, max), b: randInt(min, max) };
    case '−': {
      const a = randInt(min, max);
      const b = randInt(min, a);
      return { a, b };
    }
    case '÷': {
      const safeMin = Math.max(1, min);
      if (safeMin > max) return null;
      const b = randInt(safeMin, max);
      const k = randInt(safeMin, max);
      return { a: b * k, b };
    }
  }
}

function shuffle<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j]!, arr[i]!];
  }
  return arr;
}

function randInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
